/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import * as React from 'react';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from '@web-stories-wp/react';
import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

/**
 * Internal dependencies
 */
import { BEZIER } from '../../theme/constants';
import { CORNER_DIRECTIONS, DIRECTIONS } from '../../utils/directions';
import { Popover, Shadow } from './styled';

type CornerDirection = keyof typeof CORNER_DIRECTIONS;

const PERCENTAGE_OFFSET = {
  [DIRECTIONS.TOP]: 100,
  [DIRECTIONS.RIGHT]: 0,
  [DIRECTIONS.BOTTOM]: -100,
  [DIRECTIONS.LEFT]: -50,
};

const animationTimeSeconds = 0.175;
const transition = `transform ${animationTimeSeconds}s ${BEZIER.default}`;
const initialScale = 0.5;
const fullSize = css`
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const MenuWrapper = styled.div`
  position: absolute;
`;

const MenuRevealer = styled.div<{
  animationFinished?: boolean;
}>`
  overflow: ${({ animationFinished }) =>
    animationFinished ? 'normal' : 'hidden'};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  ${fullSize}
`;

const MenuCounterRevealer = styled.div`
  border-radius: ${({ theme }) => theme.borders.radius.small};
`;

const ButtonInner = styled(Popover)<{
  align: keyof typeof CORNER_DIRECTIONS | null;
  isReady?: boolean;
}>`
  ${fullSize}
  ${({ isInline }) => isInline && `position: relative`};
  transform: ${({ align }) => {
    switch (align) {
      case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.LEFT}`]:
        return `translate(${PERCENTAGE_OFFSET[DIRECTIONS.LEFT]}%, ${
          PERCENTAGE_OFFSET[DIRECTIONS.TOP]
        }%)`;
      case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.RIGHT}`]:
        return `translate(${PERCENTAGE_OFFSET[DIRECTIONS.RIGHT]}%, ${
          PERCENTAGE_OFFSET[DIRECTIONS.TOP]
        }%)`;
      case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.RIGHT}`]:
        return `translate(${PERCENTAGE_OFFSET[DIRECTIONS.RIGHT]}%, ${
          PERCENTAGE_OFFSET[DIRECTIONS.BOTTOM]
        }%)`;
      case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.LEFT}`]:
      default:
        return `translate(${PERCENTAGE_OFFSET[DIRECTIONS.LEFT]}%, ${
          PERCENTAGE_OFFSET[DIRECTIONS.BOTTOM]
        }%)`;
    }
  }};

  ${MenuWrapper} {
    ${(props) => {
      switch (props.align) {
        case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.RIGHT}`]:
          return css`
            top: 0;
            right: 0;
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.LEFT}`]:
          return css`
            top: 0;
            left: 0;
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.RIGHT}`]:
          return css`
            right: 0;
            bottom: 0;
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.LEFT}`]:
        default:
          return css`
            left: 0;
            bottom: 0;
          `;
      }
    }}
  }

  ${MenuRevealer} {
    transition: ${(props) => (props.isReady ? transition : 'none')};
    ${(props) => {
      const translateFromScale = 100 * (1 - initialScale);
      switch (props.align) {
        case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.RIGHT}`]:
          return css`
            transform: ${props.isReady
              ? 'none'
              : `translate(${translateFromScale}%, -${translateFromScale}%)`};
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.LEFT}`]:
          return css`
            transform: ${props.isReady
              ? 'none'
              : `translate(-${translateFromScale}%, -${translateFromScale}%)`};
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.RIGHT}`]:
          return css`
            transform: ${props.isReady
              ? 'none'
              : `translate(${translateFromScale}%, ${translateFromScale}%)`};
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.LEFT}`]:
        default:
          return css`
            transform: ${props.isReady
              ? 'none'
              : `translate(-${translateFromScale}%, ${translateFromScale}%)`};
          `;
      }
    }}
  }

  ${MenuCounterRevealer} {
    transition: ${(props) => (props.isReady ? transition : 'none')};
    ${(props) => {
      const translateFromScale = 100 * (1 - initialScale);
      switch (props.align) {
        case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.RIGHT}`]:
          return css`
            transform: ${props.isReady
              ? 'none'
              : `translate(-${translateFromScale}%, ${translateFromScale}%)`};
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.LEFT}`]:
          return css`
            transform: ${props.isReady
              ? 'none'
              : `translate(${translateFromScale}%, ${translateFromScale}%)`};
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.RIGHT}`]:
          return css`
            transform: ${props.isReady
              ? 'none'
              : `translate(-${translateFromScale}%, -${translateFromScale}%)`};
          `;
        case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.LEFT}`]:
        default:
          return css`
            transform: ${props.isReady
              ? 'none'
              : `translate(${translateFromScale}%, -${translateFromScale}%)`};
          `;
      }
    }}
  }

  ${Shadow} {
    transition: ${(props) => (props.isReady ? transition : 'none')};
    transform: ${(props) =>
      props.isReady ? 'none' : `scale(${initialScale})`};
    transform-origin: ${(props) => {
      switch (props.align) {
        case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.RIGHT}`]:
          return 'right top';
        case CORNER_DIRECTIONS[`${DIRECTIONS.TOP}_${DIRECTIONS.LEFT}`]:
          return 'left top';
        case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.RIGHT}`]:
          return 'right bottom';
        case CORNER_DIRECTIONS[`${DIRECTIONS.BOTTOM}_${DIRECTIONS.LEFT}`]:
        default:
          return 'left bottom';
      }
    }};
  }
`;

export interface AnimationContainerProps
  extends ComponentPropsWithoutRef<typeof ButtonInner> {
  isOpen?: boolean;
}

function AnimationContainer({
  children,
  isOpen,
  ...props
}: PropsWithChildren<AnimationContainerProps>) {
  const [align, setAlign] = useState<keyof typeof CORNER_DIRECTIONS | null>(
    null
  );
  const [isReady, setIsReady] = useState(false);
  const [animationFinished, setAnimationFinished] = useState(true);
  const menuPositionRef = useRef<HTMLDivElement | null>(null);
  const menuTogglePositionRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setAlign(null);
      return;
    }
    if (!(menuTogglePositionRef.current && menuPositionRef.current)) {
      return;
    }

    const toggleBoundingBox =
      menuTogglePositionRef.current.getBoundingClientRect();
    const menuWrapperBoundingBox =
      menuPositionRef.current.getBoundingClientRect();

    const menuLeft =
      toggleBoundingBox.left +
      toggleBoundingBox.width * (PERCENTAGE_OFFSET[DIRECTIONS.LEFT] / 100);
    const menuBottom =
      toggleBoundingBox.bottom +
      toggleBoundingBox.height * (PERCENTAGE_OFFSET[DIRECTIONS.BOTTOM] / 100);

    const alignHorizontal =
      menuLeft + menuWrapperBoundingBox.width > window.innerWidth
        ? DIRECTIONS.RIGHT
        : DIRECTIONS.LEFT;
    const alignVertical =
      0 > menuBottom - menuWrapperBoundingBox.height
        ? DIRECTIONS.TOP
        : DIRECTIONS.BOTTOM;

    setAlign(
      CORNER_DIRECTIONS[
        `${alignVertical}_${alignHorizontal}`
      ] as CornerDirection
    );
  }, [isOpen]);

  useEffect(() => {
    // some styles depend on the animation being finished. Set a timeout to set this variable
    // once the animation has finished.
    if (!isOpen) {
      return undefined;
    }
    setAnimationFinished(false);
    const timeoutId = setTimeout(
      () => setAnimationFinished(true),
      animationTimeSeconds * 1000
    );
    return () => clearTimeout(timeoutId);
  }, [isOpen]);

  /**
   * Seems funky, but we need 1 full render where the proper
   * alignment is set before we animate in. This prevents react
   * from batching those renders and animating from wrong alignment.
   */
  useEffect(() => {
    const frameId = requestAnimationFrame(() => setIsReady(Boolean(align)));

    return () => cancelAnimationFrame(frameId);
  }, [align]);

  return (
    <ButtonInner
      align={align}
      isOpen={isOpen}
      isReady={isReady}
      ref={menuTogglePositionRef}
      {...props}
    >
      <MenuWrapper>
        <MenuRevealer animationFinished={isOpen && animationFinished}>
          <MenuCounterRevealer ref={menuPositionRef}>
            {children}
          </MenuCounterRevealer>
        </MenuRevealer>
        <Shadow />
      </MenuWrapper>
    </ButtonInner>
  );
}

export default AnimationContainer;
